import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Globe } from 'lucide-react';

const Contact: React.FC = () => {
  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Liên Hệ - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/lien-he';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Liên Hệ VMon Cloud</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Khi có bất kỳ câu hỏi gì hãy liên hệ chúng tôi ngay! Chúng tôi luôn sẵn sàng phục vụ bạn!
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Map Section */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBDNBMTfRC0aMKHtRi_7Cw0E8-0r2cr0-g&q=VMon+Cloud"
                title="VMon Cloud Location"
                className="w-full h-96 border-0"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Thông Tin Liên Hệ</h2>
              
              <ul className="space-y-6">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-600 mt-1">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div className="ml-3 text-gray-700">
                    <p className="font-medium text-gray-900 mb-1">Địa chỉ:</p>
                    <p>Tầng 6, Tòa nhà G3, VINHOMES GREENBAY, SỐ 7, ĐCT08, Mễ Trì, Nam Từ Liêm, Hà Nội</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-600 mt-1">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div className="ml-3 text-gray-700">
                    <p className="font-medium text-gray-900 mb-1">Hotline:</p>
                    <a href="tel:+84966805315" className="text-blue-600 hover:text-blue-800">0966.805.315</a>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-600 mt-1">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div className="ml-3 text-gray-700">
                    <p className="font-medium text-gray-900 mb-1">Email:</p>
                    <p>support [at] vmon.vn</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-blue-600 mt-1">
                    <Globe className="h-6 w-6" />
                  </div>
                  <div className="ml-3 text-gray-700">
                    <p className="font-medium text-gray-900 mb-1">Công ty:</p>
                    <p>CÔNG TY TNHH CÔNG NGHỆ DỊCH VỤ VMON</p>
                  </div>
                </li>
              </ul>

              {/* Social Media */}
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Theo Dõi Chúng Tôi</h3>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.facebook.com/VMonCloud" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-600 transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://zalo.vmon.vn/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-600 transition-colors"
                  >
                    <span className="font-bold">Zalo</span>
                  </a>
                  <a 
                    href="https://t.me/VMonCloud" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.youtube.com/@VMonCloud" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/vmoncloud/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-600 transition-colors"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Gửi Tin Nhắn Cho Chúng Tôi</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Hãy điền thông tin vào form bên dưới, chúng tôi sẽ liên hệ lại với bạn trong thời gian sớm nhất.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <form className="bg-white rounded-lg shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Họ và tên
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nhập họ và tên của bạn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nhập địa chỉ email của bạn"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Tiêu đề
                </label>
                <input
                  type="text"
                  id="subject"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập tiêu đề tin nhắn"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Nội dung
                </label>
                <textarea
                  id="message"
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập nội dung tin nhắn của bạn"
                ></textarea>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  Gửi tin nhắn
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+84966805315"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Gọi ngay
            </a>
            <a
              href="https://zalo.vmon.vn/"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              Chat Zalo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;